import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  forwardRef,
} from "react";
import { GridApi } from "ag-grid-community";

interface SnFilterSearchBoxProps {
  gridRef: React.RefObject<{ api: GridApi }>;
  columnConfig?: any;
}

const SnFilterSearchBox = forwardRef(
  ({ gridRef, columnConfig }: SnFilterSearchBoxProps, ref) => {
    const [isFilterVisible, setIsFilterVisible] = useState(false);
    const filterTextRef = useRef<string>("");
    const currentIndexRef = useRef<number>(0);

    // 드래그 위치 상태 추가
    const [position, setPosition] = useState({ left: 100, top: 100 });
    const dragRef = useRef<HTMLDivElement | null>(null);
    const isDragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });

    React.useImperativeHandle(ref, () => ({
      openFilterBox: () => setIsFilterVisible(true),
      closeFilterBox: () => setIsFilterVisible(false),
    }));

    const handleKeyDown = useCallback((event: KeyboardEvent) => {
      const focusedCell = gridRef.current?.api.getFocusedCell();
      if (focusedCell && columnConfig[0].cd_grid === "grid1") {
        if (event.ctrlKey && event.key === "f") {
          event.preventDefault();
          setIsFilterVisible(true);
        }
      }
    }, []);

    useEffect(() => {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const onFilterTextBoxChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
      filterTextRef.current = e.target.value;
    };

    const findCellAndFocus = useCallback(() => {
      const gridApi = gridRef.current?.api;
      if (!filterTextRef.current || !gridApi) return;

      const columns = gridApi.getColumns();
      if (!columns) return;

      let found = false;
      let startIndex = currentIndexRef.current;

      gridApi.forEachNodeAfterFilterAndSort((rowNode, index) => {
        gridApi.clearFocusedCell();
        rowNode.setSelected(false);
        if (found || index < startIndex) return;

        const rowIndex = rowNode.rowIndex;
        const rowData = rowNode.data;

        for (const col of columns) {
          const colId = col.getColId();
          const cellValue = rowData?.[colId]?.toString();

          if (cellValue?.includes(filterTextRef.current)) {
            if (rowIndex !== null) {
              gridApi.setFocusedCell(rowIndex, colId);
              rowNode.setSelected(true);
              gridApi.ensureIndexVisible(rowIndex);
            }
            currentIndexRef.current = index + 1;
            found = true;
            break;
          }
        }
      });

      if (!found && startIndex !== 0) {
        currentIndexRef.current = 0;
        findCellAndFocus();
      }
    }, [gridRef, columnConfig]);

    const closeFilterBox = () => {
      filterTextRef.current = "";
      setIsFilterVisible(false);
    };

    const handleKeyDownInInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        findCellAndFocus();
        const inputElement = document.getElementById("filter-text-box");
        if (inputElement) {
          inputElement.focus();
        }
      }
    };

    // 드래그 이벤트 핸들러 추가
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      isDragging.current = true;
      dragOffset.current = {
        x: e.clientX - position.left,
        y: e.clientY - position.top,
      };
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      setPosition({
        left: e.clientX - dragOffset.current.x,
        top: e.clientY - dragOffset.current.y,
      });
    };

    const handleMouseUp = () => {
      isDragging.current = false;
    };

    useEffect(() => {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }, []);

    return isFilterVisible ? (
      <div
        ref={dragRef}
        style={{
          position: "absolute",
          left: `${position.left}px`,
          top: `${position.top}px`,
          background: "#fff",
          padding: "10px",
          border: "1px solid #ccc",
          zIndex: 100,
          cursor: "move",
        }}
        onMouseDown={handleMouseDown} // 드래그 시작
      >
        <input
          type="text"
          id="filter-text-box"
          onChange={onFilterTextBoxChanged}
          onKeyDown={handleKeyDownInInput}
          placeholder="검색어 입력"
        />
        <button onClick={findCellAndFocus}>찾기</button>
        <button onClick={() => setIsFilterVisible(false)}>닫기</button>
      </div>
    ) : null;
  }
);

export default React.memo(SnFilterSearchBox);
